import { SdkContext } from '../internal'
import type {
  ReviewAnswer,
  ReviewAssignment,
  ReviewAssignmentStatus,
  ReviewCalibrationView,
  ReviewCycle,
  ReviewCycleInput,
  ReviewCycleStatus,
  ReviewMatrix,
  ReviewMyNominations,
  ReviewNineBox,
  ReviewNominationsToValidate,
  ReviewPotential,
  ReviewPreflight,
  ReviewProgress,
  ReviewQuestion,
  ReviewRelationship,
  ReviewReminderResult,
  ReviewWhoReviewsWhom,
  ReviewResultDetail,
  ReviewResultSummary,
  ReviewTeamFilter
} from '@/types/performanceReview'
import type { ReviewObjectives } from '@/types/goals'

const asList = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

/**
 * F-48 Performance Review. The community comes from X-CommunityId (never the
 * path); the space from `spaceId`. What each person reads or does is the
 * server's, per cycle (`can*`, `viewerRole`); anyone else gets 403/404.
 */
export class PerformanceReviewsResource {
  constructor(private ctx: SdkContext) {}

  async listCycles(params: { spaceId?: string; status?: ReviewCycleStatus } = {}): Promise<ReviewCycle[]> {
    return asList<ReviewCycle>(await this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles' }, { query: params }))
  }

  /** The cycles and `permissions.canCreateCycle` (owner/admin), which lives outside `data`. */
  async listCyclesWithPermissions(params: { spaceId?: string; status?: ReviewCycleStatus } = {}): Promise<{ cycles: ReviewCycle[]; canCreateCycle: boolean }> {
    const body = await this.ctx.envelope<{ data?: unknown; permissions?: { canCreateCycle?: boolean } }>({ httpMethod: 'GET', path: '/performance-reviews/cycles' }, { query: params })
    return { cycles: asList<ReviewCycle>(body?.data), canCreateCycle: Boolean(body?.permissions?.canCreateCycle) }
  }

  async createCycle(body: ReviewCycleInput & { title: string; periodStartsOn: string; periodEndsOn: string; opensOn: string; closesOn: string }): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles', hasBody: true }, { body })
  }

  async getCycle(cycleId: string): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}' }, { cycleId })
  }

  async updateCycle(cycleId: string, body: ReviewCycleInput): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/performance-reviews/cycles/{cycleId}', hasBody: true }, { cycleId, body })
  }

  /** Draft only. */
  async deleteCycle(cycleId: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/performance-reviews/cycles/{cycleId}' }, { cycleId })
  }

  /** The final list, in order: with `id` edits, without creates, missing ones go. */
  async setQuestions(cycleId: string, questions: ReviewQuestion[]): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/performance-reviews/cycles/{cycleId}/questions', hasBody: true }, { cycleId, body: { questions } })
  }

  async preflight(cycleId: string): Promise<ReviewPreflight> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/preflight' }, { cycleId })
  }

  /** 409 `withoutManager` carries the list in `response.data.data.withoutManager`. */
  async open(cycleId: string): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/open' }, { cycleId })
  }

  async close(cycleId: string): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/close' }, { cycleId })
  }

  async release(cycleId: string): Promise<ReviewCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/release' }, { cycleId })
  }

  async progress(cycleId: string): Promise<ReviewProgress> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/progress' }, { cycleId })
  }

  /** Who reviews whom in a cycle (owner/admin; 403 for anyone else), without answers. */
  async whoReviewsWhom(cycleId: string): Promise<ReviewWhoReviewsWhom> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/assignments' }, { cycleId })
  }

  /** `dryRun` lists who would get it without sending. */
  async remind(cycleId: string, dryRun = false): Promise<ReviewReminderResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/reminders', hasBody: true }, { cycleId, body: { dryRun } })
  }

  async addAssignment(cycleId: string, body: { revieweeId: string; relationship: ReviewRelationship; reviewerId?: string }): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/assignments', hasBody: true }, { cycleId, body })
  }

  /** `memberId` takes the person out of the cycle (as reviewer and reviewee). */
  async cancelAssignments(cycleId: string, body: { memberId: string }): Promise<{ canceled: number }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/assignments/cancel', hasBody: true }, { cycleId, body })
  }

  async results(cycleId: string): Promise<ReviewResultSummary[]> {
    return asList<ReviewResultSummary>(await this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/results' }, { cycleId }))
  }

  async result(cycleId: string, memberId: string): Promise<ReviewResultDetail> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/results/{memberId}' }, { cycleId, memberId })
  }

  /** Only once released (before: 404). */
  async myResult(cycleId: string): Promise<ReviewResultDetail> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/my-result' }, { cycleId })
  }

  async listAssignments(params: { spaceId?: string; cycleId?: string; status?: ReviewAssignmentStatus } = {}): Promise<ReviewAssignment[]> {
    return asList<ReviewAssignment>(await this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/assignments' }, { query: params }))
  }

  async getAssignment(assignmentId: string): Promise<ReviewAssignment> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/assignments/{assignmentId}' }, { assignmentId })
  }

  /** `potential` only in the manager's review of a 9-box cycle (phase 2). */
  async saveAnswers(assignmentId: string, answers: ReviewAnswer[], potential?: ReviewPotential | null): Promise<ReviewAssignment> {
    const body = potential === undefined ? { answers } : { answers, potential }
    return this.ctx.call({ httpMethod: 'PUT', path: '/performance-reviews/assignments/{assignmentId}/answers', hasBody: true }, { assignmentId, body })
  }

  /** F-50 13: the reviewee's OKRs of the review period (self-review and manager's review only; others 403). */
  async assignmentObjectives(assignmentId: string): Promise<ReviewObjectives> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/assignments/{assignmentId}/objectives' }, { assignmentId })
  }

  /** 422 `answersMissing` carries `response.data.data.missingQuestionIds`. */
  async submit(assignmentId: string): Promise<ReviewAssignment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/assignments/{assignmentId}/submit' }, { assignmentId })
  }
  // ---------- phase 1b: peer nominations ----------

  /** The viewer's own nominations as a person reviewed in an open cycle (anyone else: 404). */
  async myNominations(cycleId: string): Promise<ReviewMyNominations> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/nominations/mine' }, { cycleId })
  }

  /** The whole list, between the cycle's min and max (422 `nominationCount`, `nomineeSelf`, `nomineeLeader`, `nomineeOutside`). */
  async nominate(cycleId: string, nomineeIds: string[]): Promise<ReviewMyNominations> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/performance-reviews/cycles/{cycleId}/nominations/mine', hasBody: true }, { cycleId, body: { nomineeIds } })
  }

  /** The direct leader: their direct reports' nominations; owner/admin: everyone's. Nobody to validate: 403. */
  async listNominations(cycleId: string, revieweeId?: string): Promise<ReviewNominationsToValidate> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/nominations' }, { cycleId, ...(revieweeId ? { query: { revieweeId } } : {}) })
  }

  /** Owner/admin: approves what is pending and gives hierarchy peers to whoever has none; validation ends today. */
  async finalizeNominations(cycleId: string): Promise<{ approved: number; hierarchyPeers: number }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/nominations/finalize' }, { cycleId })
  }

  async approveNomination(nominationId: string): Promise<ReviewNominationsToValidate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/nominations/{nominationId}/approve' }, { nominationId })
  }

  async removeNomination(nominationId: string): Promise<ReviewNominationsToValidate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/nominations/{nominationId}/remove' }, { nominationId })
  }

  async replaceNomination(nominationId: string, nomineeId: string): Promise<ReviewNominationsToValidate> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/nominations/{nominationId}/replace', hasBody: true }, { nominationId, body: { nomineeId } })
  }

  // ---------- phase 1b: calibration; phase 2: 9-box, matrix, CSV, the PDI ----------

  /** Owner/admin, cycle closed or released (before: 409 `cycleNotClosed`). */
  async calibration(cycleId: string, filter: ReviewTeamFilter = {}): Promise<ReviewCalibrationView> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/calibration' }, { cycleId, query: filter })
  }

  /** `level` and/or `potential`, always with `comment` (422 `calibrationCommentRequired`). Answers the whole calibration, unfiltered. */
  async calibrate(cycleId: string, memberId: string, body: { level?: number; potential?: ReviewPotential; comment: string }): Promise<ReviewCalibrationView> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/results/{memberId}/calibrate', hasBody: true }, { cycleId, memberId, body })
  }

  /** Owner/admin everyone, a leader their team; 409 `nineBoxOff` without the 9-box. */
  async nineBox(cycleId: string, filter: ReviewTeamFilter = {}): Promise<ReviewNineBox> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/nine-box' }, { cycleId, query: filter })
  }

  async matrix(cycleId: string, filter: ReviewTeamFilter = {}): Promise<ReviewMatrix> {
    return this.ctx.call({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/matrix' }, { cycleId, query: filter })
  }

  /** The CSV text (UTF-8 with BOM), header in the reader's language; never a comment. */
  async exportCsv(cycleId: string, filter: ReviewTeamFilter = {}): Promise<string> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/performance-reviews/cycles/{cycleId}/export' }, { cycleId, query: filter })
    const text = typeof result === 'string' ? result : String(result ?? '')
    return text.startsWith('\uFEFF') ? text : '\uFEFF' + text
  }

  /** A released result to the PDI: the person or their direct leader (others 404); 422 `pdiSpaceRequired` with several PDI spaces. */
  async createDevelopmentAction(cycleId: string, memberId: string, body: { title: string; description?: string | null; dueOn: string; spaceId?: string }): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/performance-reviews/cycles/{cycleId}/results/{memberId}/development-actions', hasBody: true }, { cycleId, memberId, body })
  }
}
